import { useState } from "react";

import { useTranslation } from "react-i18next";

import { Settings2, Sparkles } from "lucide-react";
import { toast } from "sonner";

import { useConfiguration } from "@/api/configuration";
import { useUpdatePlanningConfig } from "@/api/planningConfig";
import type { PlanningConfig } from "@/api/planningConfig";
import { Switch } from "@/components/ui/switch";

import { CleansingSection } from "./CleansingSection";
import { ForecastMethodSection } from "./ForecastMethodSection";
import { LookbackField } from "./LookbackField";
import { MethodParams } from "./MethodParams";

interface ForecastingSectionProps {
  config: PlanningConfig;
  excludedCount: number;
  onReview: () => void;
}

export function ForecastingSection({
  config,
  excludedCount,
  onReview,
}: ForecastingSectionProps) {
  const { t } = useTranslation();
  const updateConfig = useUpdatePlanningConfig();
  const configurationQuery = useConfiguration();
  const tenantLookback = configurationQuery.data?.defaultLookbackMonths ?? 36;
  const disabled = config.autoConfig ?? false;
  const [pending, setPending] = useState(false);

  function setMode(next: boolean) {
    if (next === config.autoConfig) return;
    setPending(true);
    updateConfig.mutate(
      { item: config.item, location: config.location, autoConfig: next },
      {
        onSuccess: (resp) => {
          if (next) {
            toast.success(
              t("items.detail.forecasting.autoApplied", {
                method: resp.method?.toUpperCase(),
                cleansing:
                  resp.cleansingMethod === "disabled"
                    ? t("items.detail.forecasting.cleansingOff")
                    : t("items.detail.forecasting.cleansingOn"),
                pattern:
                  resp.pattern ?? t("items.detail.forecasting.patternUnknown"),
              }),
            );
          }
        },
        onError: () =>
          toast.error(t("items.detail.forecasting.switchModeFailed")),
        onSettled: () => setPending(false),
      },
    );
  }

  function patch(p: Partial<PlanningConfig>) {
    updateConfig.mutate({ item: config.item, location: config.location, ...p });
  }

  const auto = config.autoConfig ?? false;

  return (
    <div className="flex flex-col gap-5">
      <div>
        <div className="mb-2">
          <span className="text-muted-foreground text-xs font-semibold tracking-wider uppercase">
            {t("items.detail.forecasting.title")}
          </span>
        </div>

        <div
          className={
            auto
              ? "border-primary/30 bg-accent flex items-center gap-3 rounded-lg border p-3"
              : "bg-muted flex items-center gap-3 rounded-lg border p-3"
          }
        >
          <div
            className={
              auto
                ? "bg-primary text-primary-foreground flex h-8 w-8 flex-none items-center justify-center rounded-md"
                : "bg-card text-muted-foreground flex h-8 w-8 flex-none items-center justify-center rounded-md border"
            }
          >
            {auto ? (
              <Sparkles className="size-4" />
            ) : (
              <Settings2 className="size-4" />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div
              className={
                auto
                  ? "text-accent-foreground text-sm font-semibold"
                  : "text-sm font-semibold"
              }
            >
              {auto
                ? t("items.detail.forecasting.modeAutomatic")
                : t("items.detail.forecasting.modeManual")}
            </div>
            {config.pattern ? (
              <div
                className={
                  auto
                    ? "text-accent-foreground/80 text-xs capitalize"
                    : "text-muted-foreground text-xs capitalize"
                }
              >
                {t("items.detail.forecasting.pattern", {
                  pattern: config.pattern,
                })}
              </div>
            ) : null}
          </div>

          <Switch
            checked={auto}
            onCheckedChange={setMode}
            disabled={pending}
            aria-label={t("items.detail.forecasting.autoConfigureAriaLabel")}
          />
        </div>
      </div>

      <ForecastMethodSection
        config={config}
        updateConfig={updateConfig}
        disabled={disabled}
      />

      <MethodParams config={config} disabled={disabled} onChange={patch} />

      <LookbackField
        value={config.lookbackMonths ?? null}
        tenantDefault={tenantLookback}
        onChange={(v) => patch({ lookbackMonths: v })}
        disabled={disabled}
      />

      <CleansingSection
        config={config}
        updateConfig={updateConfig}
        excludedCount={excludedCount}
        onReview={onReview}
        disabled={disabled}
      />
    </div>
  );
}
